/**
 * HeatSourceEditModal — edycja WSPÓLNEGO źródła ciepła budynku z panelu (B8, Bronek: „pokazać, GDZIE
 * jest + edycja danych kotła CO jak w tablecie", 2026-10-19): lokalizacja (pomieszczenie), parametry
 * i notatka — pola, etykiety i opcje z formularza tabletu w wariancie budynku (HeatSourceForm
 * variant='building' = pola typu z heatSourceSchema.ts + BUILDING_SOURCE_NOTE_FIELD), przez lustro
 * utils/heatSourceParams (parytet: backend/test/heatSourceParamsParity.test.js). „Charakter" zostaje
 * jak był (bez edycji tutaj).
 *
 * Komponent tylko zbiera zmiany: `onSave` dostaje WYŁĄCZNIE pola zmienione względem odczytu
 * (heatParamChanges). Zapis z kontrolą wersji (058) robi BuildingInspectionView
 * (utils/heatSourcesBase.heatSourceParamsPayload). Nowy odczyt źródła = nowy `key` u wywołującego,
 * więc formularz startuje od świeżych wartości.
 */
import React, { useState } from 'react';
import type { HeatSource } from '../types';
import { ModalShell } from '../ds/ModalShell';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import { resolveHeatSourceType } from '../ds/HeatSourceLabels';
import { HeatSourceParamsFields } from './HeatSourceParamsFields';
import { heatParamChanges } from '../utils/heatSourceParams';

interface Props {
  open: boolean;
  /** Źródło z ostatniego odczytu budynku (params = to, co widzi tablet). */
  source: HeatSource;
  saving?: boolean;
  /** Błąd zapisu (także 409 — wtedy wywołujący podaje już świeże źródło). */
  error?: string | null;
  onCancel: () => void;
  onSave: (changes: Record<string, unknown>) => void;
}

export function HeatSourceEditModal({ open, source, saving = false, error, onCancel, onSave }: Props) {
  const original = source.params || {};
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...original }));
  const set = (key: string, value: unknown) => setValues((v) => ({ ...v, [key]: value }));
  const idPrefix = `hs-edit-${source.id}`;

  const handleSave = () => onSave(heatParamChanges(original, values));
  return (
    <ModalShell
      open={open}
      title={`Edytuj źródło ciepła: ${resolveHeatSourceType(source.type)}`}
      width={600}
      onClose={() => { if (!saving) onCancel(); }}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>Anuluj</Button>
          <Button variant="primary" icon="check" onClick={handleSave} disabled={saving}>
            {saving ? 'Zapisywanie…' : 'Zapisz'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error && (
          <div
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-label)',
              background: 'var(--status-deny-bg)',
              color: 'var(--status-deny)',
            }}
          >
            <Icon name="triangle-alert" size={18} />
            <span>{error}</span>
          </div>
        )}
        <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
          Wspólne źródło budynku — te same pola co na tablecie. Lokalizacja i notatka są tylko dla nas, nie idą do CEEB.
        </p>

        <HeatSourceParamsFields idPrefix={idPrefix} type={source.type} values={values} onChange={set} disabled={saving} withNote />
      </div>
    </ModalShell>
  );
}
